import { useMemo, useState } from 'react'
import { Temporal } from 'temporal-polyfill'

import { useScheduleI18n } from '../../i18n'
import type { ScheduleEvent, ScheduleEventId } from '../../types'
import { dsText, fontSize, shadowVar, tokenVar } from '../../utils'
import {
  getWeeklyHoursEvents,
  processScheduleEvents,
  type ScheduleEventHelper,
} from '../../utils/scheduleEvent'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { TimeSelect } from './TimeSelect'
import {
  buildDateForDayAndMinutes,
  DAY_ROWS,
  DEFAULT_SLOT_END_MINUTES,
  DEFAULT_SLOT_START_MINUTES,
  END_TIME_OPTIONS,
  minutesFromZdt,
  minutesToLabel,
  SLOT_DEFAULT_DURATION_MINUTES,
  SLOT_INTERVAL_MINUTES,
  START_TIME_OPTIONS,
} from './weeklyHoursTableConstants'
import { CheckIcon, PlusIcon, TrashIcon, XIcon } from './WeeklyHoursTableIcons'

export interface WeeklyHoursTableProps {
  /** Schedule events from the API */
  events: ScheduleEvent[]
  /** Schedule timezone */
  scheduleTimeZone: string
  /** Whether editing is allowed */
  canEdit?: boolean
  /** Whether deletion is allowed */
  canDelete?: boolean
  /** Called when a new slot is created from table mode */
  onCreateSlot?: (start: Date, end: Date) => void
  /** Called when an existing slot is updated from table mode */
  onUpdateSlot?: (eventId: ScheduleEventId, start: Date, end: Date) => void
  /** Legacy callback (kept for compatibility) */
  onEditSlot?: (eventId: ScheduleEventId) => void
  /** Legacy callback (kept for compatibility) */
  onDeleteSlot?: (eventId: ScheduleEventId) => void
  /** Custom class name */
  className?: string
}

interface DaySlot {
  id: ScheduleEventId
  helper: ScheduleEventHelper
}

interface DayRow {
  dayIndex: number
  label: string
  slots: DaySlot[]
}

interface NewSlotDraft {
  id: string
  dayIndex: number
  startMinutes: number | null
  endMinutes: number | null
}

interface ExistingSlotDraft {
  startMinutes: number
  endMinutes: number
}

interface PendingDayDelete {
  dayIndex: number
  dayLabel: string
  eventIds: ScheduleEventId[]
}

interface PendingSlotDelete {
  eventId: ScheduleEventId
  dayLabel: string
}

export const WeeklyHoursTable = ({
  events,
  scheduleTimeZone,
  canEdit = false,
  canDelete = false,
  onCreateSlot,
  onUpdateSlot,
  onEditSlot: _onEditSlot,
  onDeleteSlot,
  className = '',
}: WeeklyHoursTableProps) => {
  const { text } = useScheduleI18n()
  const [newSlotDrafts, setNewSlotDrafts] = useState<NewSlotDraft[]>([])
  const [existingSlotDrafts, setExistingSlotDrafts] = useState<
    Record<ScheduleEventId, ExistingSlotDraft>
  >({})
  const [pendingDayDelete, setPendingDayDelete] =
    useState<PendingDayDelete | null>(null)
  const [pendingSlotDelete, setPendingSlotDelete] =
    useState<PendingSlotDelete | null>(null)
  const dayLabels = useMemo(
    () => [
      text('schedule.weeklyHours.table.days.sunday', 'Sundays'),
      text('schedule.weeklyHours.table.days.monday', 'Mondays'),
      text('schedule.weeklyHours.table.days.tuesday', 'Tuesdays'),
      text('schedule.weeklyHours.table.days.wednesday', 'Wednesdays'),
      text('schedule.weeklyHours.table.days.thursday', 'Thursdays'),
      text('schedule.weeklyHours.table.days.friday', 'Fridays'),
      text('schedule.weeklyHours.table.days.saturday', 'Saturdays'),
    ],
    [text],
  )
  const dayNames = useMemo(
    () => [
      text('schedule.common.weekdayLong.sun', 'Sunday'),
      text('schedule.common.weekdayLong.mon', 'Monday'),
      text('schedule.common.weekdayLong.tue', 'Tuesday'),
      text('schedule.common.weekdayLong.wed', 'Wednesday'),
      text('schedule.common.weekdayLong.thu', 'Thursday'),
      text('schedule.common.weekdayLong.fri', 'Friday'),
      text('schedule.common.weekdayLong.sat', 'Saturday'),
    ],
    [text],
  )

  const getTimeSelectLabel = (
    boundary: 'start' | 'end',
    dayIndex: number,
    rowIndex: number,
  ): string => {
    const day = dayNames[dayIndex] ?? ''
    if (boundary === 'start') {
      return rowIndex === 0
        ? text(
            'schedule.weeklyHours.table.slot.startTimeLabel',
            '{{day}} start time',
            { day },
          )
        : text(
            'schedule.weeklyHours.table.slot.startTimeLabelNumbered',
            '{{day}} start time, entry {{number}}',
            { day, number: rowIndex + 1 },
          )
    }
    return rowIndex === 0
      ? text(
          'schedule.weeklyHours.table.slot.endTimeLabel',
          '{{day}} end time',
          {
            day,
          },
        )
      : text(
          'schedule.weeklyHours.table.slot.endTimeLabelNumbered',
          '{{day}} end time, entry {{number}}',
          { day, number: rowIndex + 1 },
        )
  }

  const getAddSlotLabel = (dayIndex: number): string =>
    text('schedule.weeklyHours.table.slot.addDayLabel', 'Add {{day}} hours', {
      day: dayNames[dayIndex] ?? '',
    })

  const getDeleteSlotLabel = (
    dayIndex: number,
    startMinutes: number | null,
    endMinutes: number | null,
  ): string =>
    text(
      'schedule.weeklyHours.table.slot.deleteSlotLabel',
      'Delete {{day}} hours {{start}} to {{end}}',
      {
        day: dayNames[dayIndex] ?? '',
        start:
          startMinutes === null
            ? text('schedule.weeklyHours.table.slot.startPlaceholder', 'Start')
            : minutesToLabel(startMinutes),
        end:
          endMinutes === null
            ? text('schedule.weeklyHours.table.slot.endPlaceholder', 'End')
            : minutesToLabel(endMinutes),
      },
    )

  const dayRows = useMemo((): DayRow[] => {
    const helpers = getWeeklyHoursEvents(
      processScheduleEvents(events, scheduleTimeZone),
    )

    const grouped: Record<number, DaySlot[]> = {
      0: [],
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
    }

    helpers
      .sort((a, b) =>
        Temporal.ZonedDateTime.compare(
          a.zonedDateTimeStart,
          b.zonedDateTimeStart,
        ),
      )
      .forEach((helper) => {
        const dayIndex = helper.zonedDateTimeStart.dayOfWeek % 7
        grouped[dayIndex].push({
          id: helper.getId(),
          helper,
        })
      })

    return DAY_ROWS.map(({ dayIndex }) => ({
      dayIndex,
      label: dayLabels[dayIndex] ?? '',
      slots: grouped[dayIndex],
    }))
  }, [dayLabels, events, scheduleTimeZone])

  const getNextSlotDefaultForDay = (
    dayIndex: number,
  ): { startMinutes: number; endMinutes: number } | null => {
    const row = dayRows.find((dayRow) => dayRow.dayIndex === dayIndex)

    const existingRanges =
      row?.slots.map((slot) => {
        const draft = existingSlotDrafts[slot.id]
        return {
          startMinutes:
            draft?.startMinutes ??
            minutesFromZdt(slot.helper.zonedDateTimeStart),
          endMinutes:
            draft?.endMinutes ?? minutesFromZdt(slot.helper.zonedDateTimeEnd),
        }
      }) ?? []

    const draftRanges = newSlotDrafts
      .filter((draft) => draft.dayIndex === dayIndex)
      .map((draft) => ({
        startMinutes: draft.startMinutes,
        endMinutes: draft.endMinutes,
      }))
      .filter(
        (range): range is { startMinutes: number; endMinutes: number } =>
          range.startMinutes !== null && range.endMinutes !== null,
      )

    const allRanges = [...existingRanges, ...draftRanges]
    if (allRanges.length === 0) {
      return {
        startMinutes: DEFAULT_SLOT_START_MINUTES,
        endMinutes: DEFAULT_SLOT_END_MINUTES,
      }
    }

    const latestEndMinutes = Math.max(
      ...allRanges.map((range) => range.endMinutes),
    )

    const oneHourAfterLatest =
      Math.ceil(
        (latestEndMinutes + SLOT_DEFAULT_DURATION_MINUTES) /
          SLOT_INTERVAL_MINUTES,
      ) * SLOT_INTERVAL_MINUTES

    let startMinutes = oneHourAfterLatest

    // If a full one-hour offset does not fit, fall back to next 15-minute slot.
    if (startMinutes >= 24 * 60) {
      startMinutes = latestEndMinutes + SLOT_INTERVAL_MINUTES
    }

    if (startMinutes >= 24 * 60) {
      return null
    }

    const endMinutes = Math.min(
      startMinutes + SLOT_DEFAULT_DURATION_MINUTES,
      24 * 60,
    )

    if (endMinutes <= startMinutes) {
      return null
    }

    return { startMinutes, endMinutes }
  }

  const addDraftSlot = (dayIndex: number) => {
    const nextSlotDefault = getNextSlotDefaultForDay(dayIndex)
    if (!nextSlotDefault) return

    if (onCreateSlot) {
      const startDate = buildDateForDayAndMinutes(
        dayIndex,
        nextSlotDefault.startMinutes,
      )
      const endDate = buildDateForDayAndMinutes(
        dayIndex,
        nextSlotDefault.endMinutes,
      )
      onCreateSlot(startDate, endDate)
      return
    }

    setNewSlotDrafts((prev) => [
      ...prev,
      {
        id: `draft-${dayIndex}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        dayIndex,
        startMinutes: nextSlotDefault.startMinutes,
        endMinutes: nextSlotDefault.endMinutes,
      },
    ])
  }

  const removeDraftSlot = (draftId: string) => {
    setNewSlotDrafts((prev) => prev.filter((draft) => draft.id !== draftId))
  }

  const handleRequestDayDisable = (dayRow: DayRow) => {
    if (!canDelete || !onDeleteSlot || dayRow.slots.length === 0) return

    setPendingDayDelete({
      dayIndex: dayRow.dayIndex,
      dayLabel: dayRow.label,
      eventIds: dayRow.slots.map((slot) => slot.id),
    })
  }

  const handleConfirmDayDisable = () => {
    if (!pendingDayDelete || !onDeleteSlot) return

    pendingDayDelete.eventIds.forEach((eventId) => {
      onDeleteSlot(eventId)
    })

    setNewSlotDrafts((prev) =>
      prev.filter((draft) => draft.dayIndex !== pendingDayDelete.dayIndex),
    )
    setPendingDayDelete(null)
  }

  const handleDayToggle = (dayRow: DayRow, isOpenDay: boolean) => {
    if (isOpenDay) {
      handleRequestDayDisable(dayRow)
      return
    }

    if (!canEdit) return
    addDraftSlot(dayRow.dayIndex)
  }

  const handleRequestSlotDelete = (
    eventId: ScheduleEventId,
    dayLabel: string,
  ) => {
    if (!canDelete || !onDeleteSlot) return
    setPendingSlotDelete({ eventId, dayLabel })
  }

  const handleConfirmSlotDelete = () => {
    if (!pendingSlotDelete || !onDeleteSlot) return
    onDeleteSlot(pendingSlotDelete.eventId)
    setExistingSlotDrafts((prev) => {
      const next = { ...prev }
      delete next[pendingSlotDelete.eventId]
      return next
    })
    setPendingSlotDelete(null)
  }

  const commitExistingSlotUpdate = (
    eventId: ScheduleEventId,
    dayIndex: number,
    startMinutes: number,
    endMinutes: number,
    initialStartMinutes: number,
    initialEndMinutes: number,
  ) => {
    if (!onUpdateSlot) return
    if (startMinutes >= endMinutes) return

    const changed =
      startMinutes !== initialStartMinutes || endMinutes !== initialEndMinutes

    if (!changed) return

    const nextStart = buildDateForDayAndMinutes(dayIndex, startMinutes)
    const nextEnd = buildDateForDayAndMinutes(dayIndex, endMinutes)
    onUpdateSlot(eventId, nextStart, nextEnd)
  }

  const updateExistingSlotStart = (
    eventId: ScheduleEventId,
    dayIndex: number,
    nextStartMinutes: number,
    endMinutes: number,
    initialStartMinutes: number,
    initialEndMinutes: number,
  ) => {
    setExistingSlotDrafts((prev) => ({
      ...prev,
      [eventId]: {
        startMinutes: nextStartMinutes,
        endMinutes,
      },
    }))

    commitExistingSlotUpdate(
      eventId,
      dayIndex,
      nextStartMinutes,
      endMinutes,
      initialStartMinutes,
      initialEndMinutes,
    )
  }

  const updateExistingSlotEnd = (
    eventId: ScheduleEventId,
    dayIndex: number,
    startMinutes: number,
    nextEndMinutes: number,
    initialStartMinutes: number,
    initialEndMinutes: number,
  ) => {
    setExistingSlotDrafts((prev) => ({
      ...prev,
      [eventId]: {
        startMinutes,
        endMinutes: nextEndMinutes,
      },
    }))

    commitExistingSlotUpdate(
      eventId,
      dayIndex,
      startMinutes,
      nextEndMinutes,
      initialStartMinutes,
      initialEndMinutes,
    )
  }

  const updateDraftSlot = (
    draftId: string,
    field: 'startMinutes' | 'endMinutes',
    value: number,
  ) => {
    const currentDraft = newSlotDrafts.find((draft) => draft.id === draftId)
    if (!currentDraft) return

    const updatedDraft: NewSlotDraft = {
      ...currentDraft,
      [field]: value,
    }

    setNewSlotDrafts((prev) =>
      prev.map((draft) => (draft.id === draftId ? updatedDraft : draft)),
    )

    if (
      updatedDraft.startMinutes !== null &&
      updatedDraft.endMinutes !== null &&
      updatedDraft.startMinutes < updatedDraft.endMinutes &&
      onCreateSlot
    ) {
      const startDate = buildDateForDayAndMinutes(
        updatedDraft.dayIndex,
        updatedDraft.startMinutes,
      )
      const endDate = buildDateForDayAndMinutes(
        updatedDraft.dayIndex,
        updatedDraft.endMinutes,
      )

      onCreateSlot(startDate, endDate)

      setNewSlotDrafts((prev) => prev.filter((draft) => draft.id !== draftId))
    }
  }

  return (
    <div className={className} style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderTop: `1px solid ${tokenVar('border.default')}`,
        }}
      >
        {dayRows.map((dayRow, index) => {
          const showRowDivider = index < dayRows.length - 1
          const rowDrafts = newSlotDrafts.filter(
            (draft) => draft.dayIndex === dayRow.dayIndex,
          )
          const isOpenDay = dayRow.slots.length > 0
          const totalSlotRowsForDay = dayRow.slots.length + rowDrafts.length
          const nextSlotDefault = getNextSlotDefaultForDay(dayRow.dayIndex)
          const canAddSlotForDay = !!nextSlotDefault && canEdit

          const existingSlotValues = dayRow.slots.map((slot) => {
            const initialStartMinutes = minutesFromZdt(
              slot.helper.zonedDateTimeStart,
            )
            const initialEndMinutes = minutesFromZdt(
              slot.helper.zonedDateTimeEnd,
            )
            const draft = existingSlotDrafts[slot.id]

            return {
              id: slot.id,
              startMinutes: draft?.startMinutes ?? initialStartMinutes,
              endMinutes: draft?.endMinutes ?? initialEndMinutes,
            }
          })

          return (
            <div
              key={dayRow.dayIndex}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
                padding: '16px 0',
                borderBottom: showRowDivider
                  ? `1px solid ${tokenVar('border.default')}`
                  : 'none',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  minWidth: '170px',
                }}
              >
                <button
                  type="button"
                  onClick={() => handleDayToggle(dayRow, isOpenDay)}
                  disabled={isOpenDay ? !canDelete || !onDeleteSlot : !canEdit}
                  aria-label={
                    isOpenDay
                      ? text(
                          'schedule.weeklyHours.table.toggle.disableDay',
                          'Disable {{day}}',
                          { day: dayRow.label },
                        )
                      : text(
                          'schedule.weeklyHours.table.toggle.enableDay',
                          'Enable {{day}}',
                          { day: dayRow.label },
                        )
                  }
                  style={{
                    width: '48px',
                    height: '24px',
                    borderRadius: '999px',
                    backgroundColor: isOpenDay
                      ? tokenVar('brand.solid')
                      : tokenVar('gray.muted'),
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: isOpenDay ? 'flex-end' : 'flex-start',
                    padding: '2px',
                    boxSizing: 'border-box',
                    border: 'none',
                    cursor: isOpenDay
                      ? canDelete && onDeleteSlot
                        ? 'pointer'
                        : 'not-allowed'
                      : canEdit
                        ? 'pointer'
                        : 'not-allowed',
                  }}
                >
                  <span
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '999px',
                      backgroundColor: tokenVar('bg.default'),
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: tokenVar('fg.default'),
                      boxShadow: shadowVar('card'),
                    }}
                  >
                    {isOpenDay ? <CheckIcon /> : <XIcon />}
                  </span>
                </button>
                <span
                  style={{
                    fontFamily: dsText.Body2.fontFamily,
                    fontSize: fontSize(dsText.Body2.fontSize),
                    fontWeight: dsText.Body2.fontWeight,
                    lineHeight: dsText.Body2.lineHeight,
                    color: tokenVar('fg.muted'),
                  }}
                >
                  {dayRow.label}
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '12px',
                }}
              >
                {dayRow.slots.map((slot, slotIndex) => {
                  const initialStartMinutes = minutesFromZdt(
                    slot.helper.zonedDateTimeStart,
                  )
                  const initialEndMinutes = minutesFromZdt(
                    slot.helper.zonedDateTimeEnd,
                  )

                  const draft = existingSlotDrafts[slot.id]
                  const startMinutes =
                    draft?.startMinutes ?? initialStartMinutes
                  const endMinutes = draft?.endMinutes ?? initialEndMinutes
                  const previousEndMinutes =
                    slotIndex > 0
                      ? existingSlotValues[slotIndex - 1].endMinutes
                      : null
                  const nextStartMinutes =
                    slotIndex < existingSlotValues.length - 1
                      ? existingSlotValues[slotIndex + 1].startMinutes
                      : null

                  const startOptions = START_TIME_OPTIONS.filter(
                    (minutes) =>
                      minutes < endMinutes &&
                      (previousEndMinutes === null ||
                        minutes >= previousEndMinutes) &&
                      (nextStartMinutes === null || minutes < nextStartMinutes),
                  )
                  const endOptions = END_TIME_OPTIONS.filter(
                    (minutes) =>
                      minutes > startMinutes &&
                      (nextStartMinutes === null ||
                        minutes <= nextStartMinutes),
                  )

                  const showAddAction = slotIndex === 0
                  const showDeleteAction =
                    totalSlotRowsForDay > 1 && !showAddAction

                  return (
                    <div
                      key={slot.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '20px',
                      }}
                    >
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <TimeSelect
                          value={startMinutes}
                          options={startOptions}
                          disabled={!canEdit || !onUpdateSlot}
                          ariaLabel={getTimeSelectLabel(
                            'start',
                            dayRow.dayIndex,
                            slotIndex,
                          )}
                          placeholder={text(
                            'schedule.weeklyHours.table.slot.startPlaceholder',
                            'Start',
                          )}
                          onChange={(value) =>
                            updateExistingSlotStart(
                              slot.id,
                              dayRow.dayIndex,
                              value,
                              endMinutes,
                              initialStartMinutes,
                              initialEndMinutes,
                            )
                          }
                        />
                        <span
                          style={{
                            color: tokenVar('fg.muted'),
                            fontFamily: dsText.Body2.fontFamily,
                            fontSize: fontSize(dsText.Body2.fontSize),
                            fontWeight: dsText.Body2.fontWeight,
                            lineHeight: dsText.Body2.lineHeight,
                          }}
                        >
                          -
                        </span>
                        <TimeSelect
                          value={endMinutes}
                          options={endOptions}
                          disabled={!canEdit || !onUpdateSlot}
                          ariaLabel={getTimeSelectLabel(
                            'end',
                            dayRow.dayIndex,
                            slotIndex,
                          )}
                          placeholder={text(
                            'schedule.weeklyHours.table.slot.endPlaceholder',
                            'End',
                          )}
                          onChange={(value) =>
                            updateExistingSlotEnd(
                              slot.id,
                              dayRow.dayIndex,
                              startMinutes,
                              value,
                              initialStartMinutes,
                              initialEndMinutes,
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {showAddAction && (
                          <button
                            type="button"
                            disabled={!canAddSlotForDay || !onCreateSlot}
                            onClick={() => addDraftSlot(dayRow.dayIndex)}
                            aria-label={getAddSlotLabel(dayRow.dayIndex)}
                            title={text(
                              'schedule.weeklyHours.table.slot.addTitle',
                              'Add schedule entry',
                            )}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: 'none',
                              borderRadius: '4px',
                              background: 'transparent',
                              color: tokenVar('blue.fg'),
                              opacity:
                                canAddSlotForDay && onCreateSlot ? 1 : 0.5,
                              cursor:
                                canAddSlotForDay && onCreateSlot
                                  ? 'pointer'
                                  : 'not-allowed',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PlusIcon />
                          </button>
                        )}
                        {showDeleteAction && canDelete && onDeleteSlot && (
                          <button
                            type="button"
                            onClick={() =>
                              handleRequestSlotDelete(slot.id, dayRow.label)
                            }
                            aria-label={getDeleteSlotLabel(
                              dayRow.dayIndex,
                              startMinutes,
                              endMinutes,
                            )}
                            title={text(
                              'schedule.weeklyHours.table.slot.deleteTitle',
                              'Delete schedule entry',
                            )}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: 'none',
                              borderRadius: '4px',
                              background: 'transparent',
                              color: tokenVar('red.fg'),
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}

                {rowDrafts.map((draft, draftIndex) => {
                  const previousEndMinutes =
                    draftIndex > 0
                      ? rowDrafts[draftIndex - 1].endMinutes
                      : existingSlotValues.length > 0
                        ? existingSlotValues[existingSlotValues.length - 1]
                            .endMinutes
                        : null
                  const nextStartMinutes =
                    draftIndex < rowDrafts.length - 1
                      ? rowDrafts[draftIndex + 1].startMinutes
                      : null

                  const startOptions = START_TIME_OPTIONS.filter((minutes) => {
                    if (draft.endMinutes === null) return true
                    if (minutes >= draft.endMinutes) return false
                    if (
                      previousEndMinutes !== null &&
                      minutes < previousEndMinutes
                    ) {
                      return false
                    }
                    if (
                      nextStartMinutes !== null &&
                      minutes >= nextStartMinutes
                    ) {
                      return false
                    }
                    return true
                  })

                  const endOptions = END_TIME_OPTIONS.filter((minutes) => {
                    if (draft.startMinutes === null) return true
                    if (minutes <= draft.startMinutes) return false
                    if (
                      nextStartMinutes !== null &&
                      minutes > nextStartMinutes
                    ) {
                      return false
                    }
                    return true
                  })

                  const rowIndex = dayRow.slots.length + draftIndex
                  const showAddAction = rowIndex === 0
                  const showDeleteAction =
                    totalSlotRowsForDay > 1 && !showAddAction

                  return (
                    <div
                      key={draft.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '20px',
                      }}
                    >
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <TimeSelect
                          value={draft.startMinutes}
                          options={startOptions}
                          disabled={!canEdit || !onCreateSlot}
                          ariaLabel={getTimeSelectLabel(
                            'start',
                            dayRow.dayIndex,
                            rowIndex,
                          )}
                          placeholder={text(
                            'schedule.weeklyHours.table.slot.startPlaceholder',
                            'Start',
                          )}
                          onChange={(value) =>
                            updateDraftSlot(draft.id, 'startMinutes', value)
                          }
                        />
                        <span
                          style={{
                            color: tokenVar('fg.muted'),
                            fontFamily: dsText.Body2.fontFamily,
                            fontSize: fontSize(dsText.Body2.fontSize),
                            fontWeight: dsText.Body2.fontWeight,
                            lineHeight: dsText.Body2.lineHeight,
                          }}
                        >
                          -
                        </span>
                        <TimeSelect
                          value={draft.endMinutes}
                          options={endOptions}
                          disabled={
                            !canEdit ||
                            !onCreateSlot ||
                            draft.startMinutes === null
                          }
                          ariaLabel={getTimeSelectLabel(
                            'end',
                            dayRow.dayIndex,
                            rowIndex,
                          )}
                          placeholder={text(
                            'schedule.weeklyHours.table.slot.endPlaceholder',
                            'End',
                          )}
                          onChange={(value) =>
                            updateDraftSlot(draft.id, 'endMinutes', value)
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {showAddAction && (
                          <button
                            type="button"
                            disabled={!canAddSlotForDay}
                            onClick={() => addDraftSlot(dayRow.dayIndex)}
                            aria-label={getAddSlotLabel(dayRow.dayIndex)}
                            title={text(
                              'schedule.weeklyHours.table.slot.addTitle',
                              'Add schedule entry',
                            )}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: 'none',
                              borderRadius: '4px',
                              background: 'transparent',
                              color: tokenVar('blue.fg'),
                              opacity: canAddSlotForDay ? 1 : 0.5,
                              cursor: canAddSlotForDay
                                ? 'pointer'
                                : 'not-allowed',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PlusIcon />
                          </button>
                        )}
                        {showDeleteAction && (
                          <button
                            type="button"
                            disabled={!canEdit}
                            onClick={() => removeDraftSlot(draft.id)}
                            aria-label={getDeleteSlotLabel(
                              dayRow.dayIndex,
                              draft.startMinutes,
                              draft.endMinutes,
                            )}
                            title={text(
                              'schedule.weeklyHours.table.slot.deleteTitle',
                              'Delete schedule entry',
                            )}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: 'none',
                              borderRadius: '4px',
                              background: 'transparent',
                              color: tokenVar('red.fg'),
                              opacity: canEdit ? 1 : 0.5,
                              cursor: canEdit ? 'pointer' : 'not-allowed',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}

                {!isOpenDay && rowDrafts.length === 0 && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <span
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${tokenVar('border.default')}`,
                        backgroundColor: tokenVar('bg.default'),
                        opacity: 0.5,
                        fontFamily: dsText.Body2.fontFamily,
                        fontSize: fontSize(dsText.Body2.fontSize),
                        fontWeight: dsText.Body2.fontWeight,
                        lineHeight: dsText.Body2.lineHeight,
                        color: tokenVar('fg.muted'),
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {text(
                        'schedule.weeklyHours.table.slot.unavailable',
                        'Unavailable',
                      )}
                    </span>
                    <button
                      type="button"
                      disabled={!canAddSlotForDay || !onCreateSlot}
                      onClick={() => addDraftSlot(dayRow.dayIndex)}
                      aria-label={getAddSlotLabel(dayRow.dayIndex)}
                      title={text(
                        'schedule.weeklyHours.table.slot.addTitle',
                        'Add schedule entry',
                      )}
                      style={{
                        width: '32px',
                        height: '32px',
                        border: 'none',
                        borderRadius: '4px',
                        background: 'transparent',
                        color: tokenVar('blue.fg'),
                        opacity: canAddSlotForDay && onCreateSlot ? 1 : 0.5,
                        cursor:
                          canAddSlotForDay && onCreateSlot
                            ? 'pointer'
                            : 'not-allowed',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PlusIcon />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <DeleteConfirmationModal
        isOpen={pendingDayDelete !== null}
        onClose={() => setPendingDayDelete(null)}
        onConfirm={handleConfirmDayDisable}
        title={text(
          'schedule.weeklyHours.table.confirmDeleteDayTitle',
          'Delete time slot',
        )}
        itemName={text(
          'schedule.weeklyHours.table.confirmDeleteDayItem',
          '{{day}} weekly hours',
          { day: pendingDayDelete?.dayLabel ?? '' },
        )}
        description={
          pendingDayDelete
            ? text(
                'schedule.weeklyHours.table.confirmDeleteDayDescription',
                'Are you sure you want to delete all {{day}} time slots? This action cannot be undone.',
                { day: pendingDayDelete.dayLabel },
              )
            : undefined
        }
      />
      <DeleteConfirmationModal
        isOpen={pendingSlotDelete !== null}
        onClose={() => setPendingSlotDelete(null)}
        onConfirm={handleConfirmSlotDelete}
        title={text(
          'schedule.weeklyHours.table.confirmDeleteSlotTitle',
          'Delete time slot',
        )}
        itemName={
          pendingSlotDelete
            ? text(
                'schedule.weeklyHours.table.confirmDeleteSlotItem',
                '{{day}} time slot',
                { day: pendingSlotDelete.dayLabel },
              )
            : undefined
        }
      />
    </div>
  )
}
